import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { catchError, finalize, forkJoin, map, of } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { AvatarFactory } from 'src/app/factory/avatar-factory';
import { HttpService } from 'src/app/http/http.service';
import { ImageService } from 'src/app/services/image.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { showFallbackHttpError } from 'src/app/shared/utils/http-error-toast.util';
import {
  TENANT_CAPABILITY_API_MANAGE_PUBLIC_IP_RANGE,
  TENANT_CAPABILITY_MANAGE_PUBLIC_IP_RANGE
} from 'src/app/shared/utils/tenant-capability.util';
import { ConstantUtil } from 'src/app/util/constant.util';

import { InviteCodeModalComponent } from './invite-code-modal/invite-code-modal.component';
import { TenantIpRangeComponent } from './tenant-ip-range/tenant-ip-range.component';
import { ImageViewerComponent } from '../../avatar-modal/image-viewer/image-viewer.component';

@Component({
  selector: 'tenant-employee',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzCardModule,
    NzCheckboxModule,
    NzTableModule,
    NzTabsModule,
    NzDividerModule,
    NzButtonModule,
    NzIconModule,
    NzInputModule,
    NzFormModule,
    NzModalModule,
    NzSelectModule,
    NzSpinModule,
    NzTagModule,
    NzToolTipModule,
    NzPopconfirmModule,
    NzGridModule,
    NzAvatarModule,
    NzPaginationModule,
    TenantIpRangeComponent,
    ImageViewerComponent,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './tenant-employee.component.html',
  styleUrls: ['./tenant-employee.component.less']
})
export class TenantEmployeeComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() tenantId: string = '';
  @Input() isSelector: boolean = false;
  @Input() tenantType?: string;
  @Input() embeddedMode = false;
  @Output() readonly employeeSelected = new EventEmitter<any>();

  // 列表数据
  employeeList: any[] = [];
  loading = false;
  total = 0;
  pageSize = 20;
  pageIndex = 1;
  searchKeyword = '';
  direction = 'desc';
  refreshTime = Date.now();
  addEmployeeLoading = false;
  isConfirmLoading = false;
  isEditing = false;
  currentEmployeeId: string = '';
  employeeForm!: FormGroup;
  imageViewerVisible = false;
  avatarUrl = '';
  shouldShowAddButton = false;
  shouldShowRoleColumn = false;
  isMobileView = false;
  isLoadingMore = false;
  hasMoreMobile = false;
  mobileAccessUpdatingIds = new Set<string>();
  // 批量名单检索状态 (AL-3390)
  batchModalVisible = false;
  batchRawText = '';
  batchKeywords: string[] = [];
  batchKeywordsCount = 0;
  batchKeywordsExceeded = false;
  unmatchedKeys: string[] = [];
  batchMatchedCount = 0;
  showUnmatchedPanel = false;
  isBatchResultActive = false;

  // 邀请人筛选与批量选择状态 (AL-3390)
  inviterList: Array<{ accountId: string; name: string; avatarId?: string; invitedCount?: number }> = [];
  selectedInviterId: string | null = null;
  selectedIds = new Set<string>();
  isSelectAllAcrossPages = false;
  excludedIds = new Set<string>();
  batchMobileAccessModalVisible = false;
  batchMobileAccessValue = true;
  batchUpdating = false;
  loginAccountId = '';

  private _hasPublicIpCapability = false;

  get hasPublicIpCapability(): boolean {
    if (this.isInModal || this.isPersonalTenant) {
      return false;
    }
    return this._hasPublicIpCapability;
  }

  set hasPublicIpCapability(val: boolean) {
    this._hasPublicIpCapability = val;
  }
  selectedTabIndex = 0;

  get effectiveTenantId(): string {
    return this.tenantId || this.currentTenantId;
  }

  readonly mobileSkeletonCards = Array.from({ length: 3 });
  @ViewChild('mobileScrollContainer') mobileScrollContainer?: ElementRef<HTMLElement>;
  @ViewChild('mobileLoadMoreAnchor') mobileLoadMoreAnchor?: ElementRef<HTMLDivElement>;
  private currentTenantId = '';
  private currentTenantType = '';
  private mobileLoadObserver?: IntersectionObserver;

  private readonly http = inject(HttpService);
  private readonly message = inject(NzMessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly modal = inject(NzModalService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly ngZone = inject(NgZone);

  private readonly modalRef = inject(NzModalRef, { optional: true });
  private readonly imageService = inject(ImageService);
  private readonly route = inject(ActivatedRoute, { optional: true });
  private readonly router = inject(Router, { optional: true });

  private readonly nzModalData = inject(NZ_MODAL_DATA, { optional: true }) || {};

  ngOnInit(): void {
    if (this.nzModalData.tenantId) {
      this.tenantId = this.nzModalData.tenantId;
    }
    if (this.nzModalData.isSelector !== undefined) {
      this.isSelector = this.nzModalData.isSelector;
    }

    this.initLocalContext();
    this.initForm();
    this.updateViewportMode();
    this.checkCapability();
    this.syncTabFromRoute();
    this.loadEmployeeList();
    this.loadInviterList();
  }

  checkCapability(): void {
    if (this.isInModal || this.isPersonalTenant) {
      this.hasPublicIpCapability = false;
      return;
    }
    const currentTenant = this.readLocalJson('current_tenant');
    const targetTenantId = this.effectiveTenantId;
    if (!targetTenantId) {
      return;
    }
    if (currentTenant && currentTenant.id === targetTenantId && Array.isArray(currentTenant.capabilityCodes)) {
      this.hasPublicIpCapability =
        currentTenant.capabilityCodes.includes(TENANT_CAPABILITY_MANAGE_PUBLIC_IP_RANGE) ||
        currentTenant.capabilityCodes.includes(TENANT_CAPABILITY_API_MANAGE_PUBLIC_IP_RANGE) ||
        currentTenant.capabilityCodes.includes('DeviceNetworkAccessControl') ||
        currentTenant.capabilityCodes.includes('tenant.deviceNetworkAccessControl') ||
        currentTenant.capabilityCodes.includes('ClientAccessControl') ||
        currentTenant.capabilityCodes.includes('PublicIpRangeManage');
    }
    this.http.post(`${ConstantUtil.TENANT_CAPABILITY_V1_ITEM}/${targetTenantId}`, {}).subscribe({
      next: res => {
        if (this.isPersonalTenant) {
          this.hasPublicIpCapability = false;
          this.syncTabFromRoute();
          this.cdr.detectChanges();
          return;
        }
        const capabilities: string[] = res?.data?.capabilities ?? [];
        this.hasPublicIpCapability =
          capabilities.includes(TENANT_CAPABILITY_API_MANAGE_PUBLIC_IP_RANGE) ||
          capabilities.includes(TENANT_CAPABILITY_MANAGE_PUBLIC_IP_RANGE) ||
          capabilities.includes('DeviceNetworkAccessControl') ||
          capabilities.includes('tenant.deviceNetworkAccessControl') ||
          capabilities.includes('ClientAccessControl') ||
          capabilities.includes('PublicIpRangeManage');
        if (!this.hasPublicIpCapability) {
          this.clearSelection();
        }
        this.syncTabFromRoute();
        this.cdr.detectChanges();
      },
      error: () => {}
    });
  }

  syncTabFromRoute(): void {
    if (this.isPersonalTenant || this.isInModal) {
      this.hasPublicIpCapability = false;
      this.selectedTabIndex = 0;
      return;
    }
    const tab = this.route?.snapshot?.queryParams?.['tab'];
    if (tab === 'ip-range' && this.hasPublicIpCapability && !this.isInModal) {
      this.selectedTabIndex = 1;
    } else if (tab === 'members' || !this.hasPublicIpCapability || this.isInModal) {
      this.selectedTabIndex = 0;
    }
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
    if (this.router && this.route) {
      const tabName = index === 1 ? 'ip-range' : 'members';
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab: tabName },
        queryParamsHandling: 'merge'
      });
    }
  }

  ngAfterViewInit(): void {
    this.syncMobileLoadObserver();
  }

  ngOnDestroy(): void {
    this.mobileLoadObserver?.disconnect();
  }

  private initLocalContext(): void {
    const currentTenant = this.readLocalJson('current_tenant');
    const loginInfo = this.readLocalJson('loginInfo');
    this.currentTenantId = currentTenant?.id ?? '';
    this.currentTenantType = currentTenant?.type ?? '';
    this.loginAccountId = loginInfo?.accountId ?? '';
    this.shouldShowAddButton = !this.isInModal && this.currentTenantId === this.tenantId;
    this.shouldShowRoleColumn = this.currentTenantType === 'Person';
  }

  private readLocalJson(key: string): any | null {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private decorateEmployee(employee: any): any {
    const isCurrentLoginEmployee = !!this.loginAccountId && this.loginAccountId === employee?.accountId;
    const joinedAt = employee?.joinTime ?? employee?.createTime ?? employee?.joinedAt;
    const roleKey = this.isPersonalTenant
      ? isCurrentLoginEmployee
        ? 'tenant.employee.role-owner'
        : 'tenant.employee.role-secretary'
      : (employee?.roleKey ?? (employee?.role === 'OWNER' || employee?.isOwner ? 'tenant.employee.role-owner' : undefined));
    return {
      ...employee,
      joinedAt,
      fallbackAvatarUrl: AvatarFactory.defaultAvatarWithName(employee?.name || employee?.accountId || 'U'),
      roleKey,
      canExitTeam: !this.isSelector && employee?.status === 'Enable' && (this.currentTenantType !== 'Person' || !isCurrentLoginEmployee)
    };
  }

  private preloadAvatarUrls(employees: any[]): void {
    const requests = employees
      .filter(employee => employee?.avatarId && !employee?.avatarUrl)
      .map(employee =>
        this.imageService.loadImage(employee.avatarId, 'M').pipe(
          map(imageUrl => ({ employee, imageUrl })),
          catchError(() => of({ employee, imageUrl: '' }))
        )
      );

    if (!requests.length) {
      return;
    }

    forkJoin(requests).subscribe(results => {
      results.forEach(({ employee, imageUrl }) => {
        if (imageUrl) {
          employee.avatarUrl = imageUrl;
        }
      });
      this.cdr.detectChanges();
    });
  }

  // 初始化表单
  initForm(): void {
    this.employeeForm = this.fb.group({
      name: [null, [Validators.required]],
      status: ['Enable']
    });
  }
  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewportMode(true);
  }

  private updateViewportMode(shouldReload = false): void {
    const nextMobile = typeof window !== 'undefined' && window.innerWidth <= 767;
    const modeChanged = this.isMobileView !== nextMobile;
    this.isMobileView = nextMobile;

    if (modeChanged && shouldReload && this.tenantId) {
      this.pageIndex = 1;
      this.refreshTime = Date.now();
      this.loadEmployeeList();
    } else {
      this.syncMobileLoadObserver();
    }
  }

  private updateMobileLoadState(): void {
    this.hasMoreMobile = this.isMobileView && this.employeeList.length < this.total;
    this.syncMobileLoadObserver();
  }

  private syncMobileLoadObserver(): void {
    this.mobileLoadObserver?.disconnect();

    if (!this.isMobileView || !this.hasMoreMobile || !this.mobileLoadMoreAnchor?.nativeElement) {
      return;
    }

    this.mobileLoadObserver = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.ngZone.run(() => this.loadMoreOnMobile());
        }
      },
      {
        root: this.mobileScrollContainer?.nativeElement ?? null,
        rootMargin: '0px 0px 240px 0px',
        threshold: 0.01
      }
    );

    this.mobileLoadObserver.observe(this.mobileLoadMoreAnchor.nativeElement);
  }

  loadEmployeeList(options?: { append?: boolean; pageIndex?: number }): void {
    if (!this.tenantId) {
      return;
    }

    const append = !!options?.append;
    const requestPageIndex = options?.pageIndex ?? this.pageIndex;

    if (append) {
      this.isLoadingMore = true;
    } else {
      this.loading = true;
    }
    const isBatch = this.batchKeywords.length > 0;
    const params = {
      tenantId: this.tenantId,
      pageIndex: requestPageIndex - 1,
      pageSize: this.pageSize,
      direction: this.direction,
      refreshTime: this.refreshTime,
      ...(isBatch ? { keys: this.batchKeywords } : { key: this.searchKeyword.trim() }),
      ...(this.isSelector ? { status: 'Enable' } : {}),
      ...(this.selectedInviterId ? { invitedByAccountId: this.selectedInviterId } : {})
    };

    this.http
      .post(ConstantUtil.TENANT_EMPLOYEE_LIST, params)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.isLoadingMore = false;
          this.cdr.detectChanges();
          this.syncMobileLoadObserver();
        })
      )
      .subscribe(res => {
        if (res.success && res.data) {
          const items = this.isSelector
            ? (res.data.items || []).filter((employeeItem: any) => employeeItem.status === 'Enable')
            : res.data.items || [];
          const decoratedItems = items.map((employeeItem: any) => this.decorateEmployee(employeeItem));
          this.pageIndex = requestPageIndex;
          this.employeeList = append ? [...this.employeeList, ...decoratedItems] : decoratedItems;
          this.preloadAvatarUrls(decoratedItems);
          this.total = res.data.totalCount;
          this.unmatchedKeys = res.data.unmatchedKeys || [];
          this.batchMatchedCount = res.data.matchedCount ?? (this.total > 0 ? this.batchKeywords.length - this.unmatchedKeys.length : 0);
          this.isBatchResultActive = isBatch;
          this.updateMobileLoadState();
          this.cdr.detectChanges();
        } else {
          showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('tenant.employee.load-list-failed'));
          this.updateMobileLoadState();
        }
      });
  }

  loadMoreOnMobile(): void {
    if (!this.isMobileView || this.loading || this.isLoadingMore || !this.hasMoreMobile) {
      return;
    }

    this.loadEmployeeList({
      append: true,
      pageIndex: this.pageIndex + 1
    });
  }
  // 添加选择员工的方法
  selectEmployee(employee: any): void {
    this.employeeSelected.emit(employee);
    if (this.modalRef) {
      this.modalRef.close(employee);
    }
  }

  onSearchEnter(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (!keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      this.search();
    }
  }

  // 搜索
  search(): void {
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.clearSelection();

    const raw = this.searchKeyword || '';
    const parsed = this.parseBatchKeywords(raw);
    const hasDelimiters = /[\n,;\uFF0C\uFF1B]/.test(raw);

    if (parsed.keywords.length > 1 || (parsed.keywords.length === 1 && hasDelimiters)) {
      if (parsed.exceeds) {
        this.message.warning(this.i18n.fanyi('tenant.employee.batch-limit-exceeded', { count: parsed.count }));
        return;
      }
      this.batchKeywords = parsed.keywords;
      this.isBatchResultActive = true;
    } else if (parsed.keywords.length === 1) {
      this.batchKeywords = [];
      this.isBatchResultActive = false;
    } else {
      this.batchKeywords = [];
      this.isBatchResultActive = false;
    }

    this.loadEmployeeList();
  }

  resetSearch(): void {
    this.searchKeyword = '';
    this.batchKeywords = [];
    this.batchRawText = '';
    this.batchKeywordsCount = 0;
    this.batchKeywordsExceeded = false;
    this.unmatchedKeys = [];
    this.batchMatchedCount = 0;
    this.showUnmatchedPanel = false;
    this.isBatchResultActive = false;
    this.selectedInviterId = null;
    this.clearSelection();
    this.loadEmployeeList();
  }

  loadInviterList(): void {
    if (!this.tenantId) {
      return;
    }
    this.http.post(ConstantUtil.TENANT_EMPLOYEE_INVITER_LIST, { tenantId: this.tenantId }).subscribe(res => {
      if (res.success && res.data) {
        this.inviterList = Array.isArray(res.data) ? res.data : Array.isArray(res.data.items) ? res.data.items : [];
        this.cdr.detectChanges();
      }
    });
  }

  onInviterChange(inviterId: string | null): void {
    this.selectedInviterId = inviterId;
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.clearSelection();
    this.loadEmployeeList();
  }

  filterByInviter(inviterAccountId: string | null, _inviterName?: string): void {
    this.selectedInviterId = inviterAccountId;
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.clearSelection();
    this.loadEmployeeList();
  }

  clearSelection(): void {
    this.selectedIds.clear();
    this.isSelectAllAcrossPages = false;
    this.excludedIds.clear();
  }

  get selectedCount(): number {
    if (this.isPersonalTenant) {
      return 0;
    }
    if (this.isSelectAllAcrossPages) {
      return Math.max(0, this.total - this.excludedIds.size);
    }
    return this.selectedIds.size;
  }

  get isAllCurrentPageSelected(): boolean {
    if (this.isPersonalTenant || !this.employeeList || this.employeeList.length === 0) {
      return false;
    }
    return this.employeeList.every(emp => this.isRowSelected(emp));
  }

  get isCurrentPageIndeterminate(): boolean {
    if (this.isPersonalTenant || !this.employeeList || this.employeeList.length === 0) {
      return false;
    }
    const hasAny = this.employeeList.some(emp => this.isRowSelected(emp));
    return hasAny && !this.isAllCurrentPageSelected;
  }

  isRowSelected(employee: any): boolean {
    if (this.isPersonalTenant || !employee || !employee.id) {
      return false;
    }
    if (this.isSelectAllAcrossPages) {
      return !this.excludedIds.has(employee.id);
    }
    return this.selectedIds.has(employee.id);
  }

  onRowSelect(employee: any, checked: boolean): void {
    if (this.isPersonalTenant || !this.hasPublicIpCapability || !employee || !employee.id) {
      return;
    }
    if (this.isSelectAllAcrossPages) {
      if (checked) {
        this.excludedIds.delete(employee.id);
      } else {
        this.excludedIds.add(employee.id);
      }
    } else {
      if (checked) {
        this.selectedIds.add(employee.id);
      } else {
        this.selectedIds.delete(employee.id);
      }
    }
  }

  onSelectAllCurrentPage(checked: boolean): void {
    if (this.isPersonalTenant || !this.hasPublicIpCapability || !this.employeeList) {
      return;
    }
    if (this.isSelectAllAcrossPages) {
      this.employeeList.forEach(emp => {
        if (checked) {
          this.excludedIds.delete(emp.id);
        } else {
          this.excludedIds.add(emp.id);
        }
      });
    } else {
      this.employeeList.forEach(emp => {
        if (checked) {
          this.selectedIds.add(emp.id);
        } else {
          this.selectedIds.delete(emp.id);
        }
      });
    }
  }

  selectAllAcrossPages(): void {
    if (this.isPersonalTenant || !this.hasPublicIpCapability) {
      return;
    }
    this.isSelectAllAcrossPages = true;
    this.excludedIds.clear();
    this.selectedIds.clear();
  }

  isOwnerEmployee(employee: any): boolean {
    if (!employee) {
      return false;
    }
    if (this.isPersonalTenant) {
      return (
        (this.loginAccountId && employee.accountId === this.loginAccountId) ||
        employee.roleKey === 'tenant.employee.role-owner' ||
        employee.role === 'OWNER' ||
        employee.isOwner === true
      );
    }
    return employee.roleKey === 'tenant.employee.role-owner' || employee.role === 'OWNER' || employee.isOwner === true;
  }

  openBatchMobileAccessModal(enabled: boolean): void {
    if (this.selectedCount === 0) {
      return;
    }
    this.batchMobileAccessValue = enabled;

    if (this.isSelectAllAcrossPages && this.total > this.employeeList.length) {
      this.batchUpdating = true;
      const isBatch = this.batchKeywords.length > 0;
      const fetchParams = {
        tenantId: this.tenantId,
        pageIndex: 0,
        pageSize: Math.min(this.total, 200),
        direction: this.direction,
        refreshTime: this.refreshTime,
        ...(isBatch ? { keys: this.batchKeywords } : { key: this.searchKeyword.trim() }),
        ...(this.isSelector ? { status: 'Enable' } : {}),
        ...(this.selectedInviterId ? { invitedByAccountId: this.selectedInviterId } : {})
      };
      this.http
        .post(ConstantUtil.TENANT_EMPLOYEE_LIST, fetchParams)
        .pipe(
          finalize(() => {
            this.batchUpdating = false;
            this.cdr.detectChanges();
          })
        )
        .subscribe(res => {
          if (res.success && res.data?.items) {
            this.executeBatchMobileAccessModal(res.data.items, enabled);
          } else {
            this.executeBatchMobileAccessModal(this.employeeList, enabled);
          }
        });
      return;
    }

    this.executeBatchMobileAccessModal(this.employeeList, enabled);
  }

  private executeBatchMobileAccessModal(candidateSource: any[], enabled: boolean): void {
    let candidateEmployees: any[] = [];
    if (this.isSelectAllAcrossPages) {
      candidateEmployees = candidateSource.filter(emp => !this.excludedIds.has(emp.id));
    } else {
      candidateEmployees = candidateSource.filter(emp => this.selectedIds.has(emp.id));
    }

    const hasOwner = candidateEmployees.some(emp => this.isOwnerEmployee(emp));
    const targetEmployees = candidateEmployees.filter(emp => !this.isOwnerEmployee(emp));

    if (targetEmployees.length === 0) {
      this.message.warning(this.i18n.fanyi('tenant.employee.batch-modal-owner-excluded'));
      return;
    }

    const count = targetEmployees.length;
    const policyText = enabled ? this.i18n.fanyi('tenant.employee.access-allow') : this.i18n.fanyi('tenant.employee.access-disable');

    let content = this.i18n.fanyi('tenant.employee.batch-confirm-content', { count, policy: policyText });
    if (hasOwner) {
      content += ` ${this.i18n.fanyi('tenant.employee.batch-modal-owner-excluded')}`;
    }

    this.modal.confirm({
      nzTitle: this.i18n.fanyi('tenant.employee.batch-confirm-title'),
      nzContent: content,
      nzOkText: this.i18n.fanyi('common.confirm'),
      nzCancelText: this.i18n.fanyi('common.cancel'),
      nzOnOk: () =>
        this.confirmBatchMobileAccess(
          targetEmployees.map(e => e.id),
          enabled
        )
    });
  }

  confirmBatchMobileAccess(employeeIds: string[], enabled: boolean): void {
    if (!employeeIds.length || !this.tenantId) {
      return;
    }
    this.batchUpdating = true;
    this.http
      .post(ConstantUtil.TENANT_EMPLOYEE_MOBILE_ACCESS_BATCH_SET, {
        tenantId: this.tenantId,
        employeeIds,
        mobileAccessEnabled: enabled
      })
      .pipe(
        finalize(() => {
          this.batchUpdating = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          if (res.data?.failedCount && res.data.failedCount > 0) {
            this.message.warning(
              `${this.i18n.fanyi('tenant.employee.batch-update-partial-failed')} (${res.data.failedCount}/${res.data.totalRequested})`
            );
          } else {
            this.message.success(this.i18n.fanyi('tenant.employee.batch-update-success'));
          }
          const failedIdSet = new Set((res.data?.failedItems || []).map((item: any) => item.employeeId));
          const successIdSet = new Set(employeeIds.filter(id => !failedIdSet.has(id)));
          this.employeeList.forEach(emp => {
            if (successIdSet.has(emp.id)) {
              emp.mobileAccessEnabled = enabled;
            }
          });
          this.clearSelection();
          // 重設分頁快照，避免剛更新的成員因 updateTime 晚於舊快照而被列表查詢排除。
          this.refreshTime = 0;
          this.loadEmployeeList();
        } else {
          showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('tenant.employee.mobile-access-update-failed'));
        }
      });
  }

  toggleUnmatchedPanel(): void {
    this.showUnmatchedPanel = !this.showUnmatchedPanel;
  }

  clearBatchSearch(): void {
    this.batchKeywords = [];
    this.batchRawText = '';
    this.searchKeyword = '';
    this.batchKeywordsCount = 0;
    this.batchKeywordsExceeded = false;
    this.unmatchedKeys = [];
    this.batchMatchedCount = 0;
    this.showUnmatchedPanel = false;
    this.isBatchResultActive = false;
    this.clearSelection();
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadEmployeeList();
  }

  openBatchModal(): void {
    this.batchModalVisible = true;
    this.batchRawText = this.batchKeywords.join('\n');
    this.onBatchRawTextChange(this.batchRawText);
  }

  closeBatchModal(): void {
    this.batchModalVisible = false;
  }

  onBatchRawTextChange(text: string): void {
    this.batchRawText = text;
    const parsed = this.parseBatchKeywords(text);
    this.batchKeywordsCount = parsed.count;
    this.batchKeywordsExceeded = parsed.exceeds;
  }

  confirmBatchModal(): void {
    const parsed = this.parseBatchKeywords(this.batchRawText);
    if (parsed.exceeds) {
      return;
    }
    this.batchKeywords = parsed.keywords;
    this.searchKeyword = this.batchKeywords.join('\n');
    this.isBatchResultActive = this.batchKeywords.length > 0;
    this.batchModalVisible = false;
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.clearSelection();
    this.loadEmployeeList();
  }

  clearBatchModalInput(): void {
    this.batchRawText = '';
    this.batchKeywordsCount = 0;
    this.batchKeywordsExceeded = false;
  }

  clearBatchKeywords(): void {
    this.batchKeywords = [];
    this.batchRawText = '';
    this.batchKeywordsCount = 0;
    this.batchKeywordsExceeded = false;
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadEmployeeList();
  }

  /**
   * 解析纯文本多值名单字符串（以逗号、换行、空格、分号切分，去重并修剪）
   */
  parseBatchKeywords(raw: string): { keywords: string[]; count: number; exceeds: boolean } {
    if (!raw || !raw.trim()) {
      return { keywords: [], count: 0, exceeds: false };
    }
    const rawTokens = raw.split(/[,，;\uFF1B\s\n\r]+/);
    const set = new Set<string>();
    rawTokens.forEach(token => {
      const trimmed = token.trim();
      if (trimmed) {
        set.add(trimmed);
      }
    });
    const keywords = Array.from(set);
    return {
      keywords,
      count: keywords.length,
      exceeds: keywords.length > 200
    };
  }

  /**
   * 判斷是否為個人租戶。
   */
  get isPersonalTenant(): boolean {
    return this.tenantType === 'Person' || this.currentTenantType === 'Person';
  }

  /**
   * 判斷租戶成員是否可編輯手機端存取設定。
   * 個人租戶全員不可編輯（強制允許），未開啟客戶端安全存取管控能力時不可編輯，開啟後非個人租戶可編輯。
   */
  isMobileAccessEditable(employee: any): boolean {
    if (this.isSelector || this.isPersonalTenant || !this.hasPublicIpCapability) {
      return false;
    }
    return true;
  }

  /**
   * 判斷指定租戶成員的手機端存取設定是否更新中。
   */
  isMobileAccessUpdating(employeeId: string): boolean {
    return this.mobileAccessUpdatingIds.has(employeeId);
  }

  /**
   * 更新租戶成員手機端存取設定；成功時重新載入列表，失敗時還原畫面狀態。
   */
  updateMobileAccess(employee: any, enabled: boolean): void {
    if (!this.isMobileAccessEditable(employee) || this.isMobileAccessUpdating(employee.id)) {
      return;
    }

    const previousValue = employee.mobileAccessEnabled !== false;
    employee.mobileAccessEnabled = enabled;
    this.mobileAccessUpdatingIds = new Set(this.mobileAccessUpdatingIds).add(employee.id);

    this.http
      .post(ConstantUtil.TENANT_EMPLOYEE_MOBILE_ACCESS_SET, {
        tenantId: this.tenantId,
        employeeId: employee.id,
        mobileAccessEnabled: enabled
      })
      .pipe(
        finalize(() => {
          const updatingIds = new Set(this.mobileAccessUpdatingIds);
          updatingIds.delete(employee.id);
          this.mobileAccessUpdatingIds = updatingIds;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (res.success) {
            this.message.success(this.i18n.fanyi('tenant.employee.mobile-access-update-success'));
            // 重設分頁快照，避免剛更新的成員因 updateTime 晚於舊快照而被列表查詢排除。
            this.refreshTime = 0;
            this.loadEmployeeList();
            return;
          }
          employee.mobileAccessEnabled = previousValue;
          this.message.error(res.msg ?? this.i18n.fanyi('tenant.employee.mobile-access-update-failed'));
        },
        error: error => {
          employee.mobileAccessEnabled = previousValue;
          this.message.error(error?.error?.msg ?? this.i18n.fanyi('tenant.employee.mobile-access-update-failed'));
        }
      });
  }

  // 分页变化
  pageIndexChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.loadEmployeeList();

    setTimeout(() => {
      const tableBody = document.querySelector('.fixed-header-table .ant-table-body');
      if (tableBody) {
        tableBody.scrollTop = 0;
      }
    }, 100);
  }

  pageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadEmployeeList();
  }
  // 添加行点击选择方法
  selectRow(employee: any): void {
    if (this.isSelector) {
      this.selectEmployee(employee);
    }
  }

  deleteEmployee(data: any): void {
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('tenant.employee.confirm-delete'),
      nzContent: this.i18n.fanyi('tenant.employee.confirm-delete-content', { name: data.name }),
      nzOkText: this.i18n.fanyi('tenant.employee.confirm'),
      nzOkType: 'primary',
      nzOkDanger: true,
      nzOnOk: () => this.confirmDelete(data.id),
      nzCancelText: this.i18n.fanyi('tenant.employee.cancel')
    });
  }

  confirmDelete(id: string): void {
    this.loading = true;
    this.http
      .post(`${ConstantUtil.TENANT_EMPLOYEE_DELETE}/${id}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.message.success(this.i18n.fanyi('tenant.employee.delete-success'));
          this.refreshTime = Date.now();
          this.pageIndex = 1;
          this.loadEmployeeList();
        } else {
          showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('tenant.employee.delete-failed'));
        }
      });
  }
  addEmployee(): void {
    let isPersonalTenant = false;
    let currentTenant: any = null;
    const tenantStr = localStorage.getItem('current_tenant');

    if (tenantStr) {
      try {
        currentTenant = JSON.parse(tenantStr);
      } catch (error) {
        this.message.error(this.i18n.fanyi('tenant.employee.load-tenant-failed'));
        return;
      }
    }

    if (this.tenantType) {
      isPersonalTenant = this.tenantType === 'Person';
    } else {
      isPersonalTenant = currentTenant?.type === 'Person';
    }

    let bossSnid: string | null = null;
    if (isPersonalTenant) {
      bossSnid = localStorage.getItem('current_tenant_boss_snid');
      if (!bossSnid) {
        this.message.error(this.i18n.fanyi('tenant.employee.business-number-not-exist'));
        return;
      }
    }

    const targetTenantId = this.tenantId || currentTenant?.id || '';

    if (isPersonalTenant) {
      this.addEmployeeLoading = true;
      const args: any = {
        type: 'JoinServiceNumber',
        serviceNumberId: bossSnid
      };
      if (targetTenantId) {
        args.tenantId = targetTenantId;
      }

      this.http
        .post(ConstantUtil.TENANT_INVITATION_V1_CREATE, args)
        .pipe(
          finalize(() => {
            this.addEmployeeLoading = false;
            this.cdr.detectChanges();
          })
        )
        .subscribe({
          next: res => {
            if (res.success && res.data) {
              if (res.data.warnMessage) {
                this.message.warning(res.data.warnMessage);
              }
              this.modal.create({
                nzTitle: this.i18n.fanyi('tenant.employee.add-employee-title'),
                nzContent: InviteCodeModalComponent,
                nzData: {
                  inviteCode: res.data.code,
                  isPersonalTenant: true
                },
                nzWidth: 600,
                nzClassName: 'tenant-invite-dialog',
                nzWrapClassName: 'tenant-invite-modal',
                nzFooter: null,
                nzMaskClosable: false
              });
            } else {
              showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('tenant.employee.operation-failed'));
            }
          },
          error: err => {
            showFallbackHttpError(this.message, err?.error?.msg, this.i18n.fanyi('tenant.employee.operation-failed'));
          }
        });
      return;
    }

    this.modal.create({
      nzTitle: this.i18n.fanyi('tenant.employee.add-employee-title'),
      nzContent: InviteCodeModalComponent,
      nzData: {
        tenantId: targetTenantId,
        isPersonalTenant: false,
        hasDeviceNetworkAccessControlCapability: this.hasPublicIpCapability,
        hasClientAccessControlCapability: this.hasPublicIpCapability
      },
      nzWidth: 600,
      nzClassName: 'tenant-invite-dialog',
      nzWrapClassName: 'tenant-invite-modal',
      nzFooter: null,
      nzMaskClosable: false
    });
  }
  openImageViewer(imageUrl: string, employee?: any): void {
    // 如果没有真实的头像URL，使用员工姓名生成文字头像
    if (!imageUrl && employee?.name) {
      this.avatarUrl = AvatarFactory.defaultAvatarWithName(employee.name);
    } else {
      this.avatarUrl = imageUrl;
    }
    this.imageViewerVisible = true;
  }
  closeImageViewer(): void {
    this.avatarUrl = '';
    this.imageViewerVisible = false;
  }

  // 判断是否在模态框中或租户ID不匹配
  get isInModal(): boolean {
    return !!this.modalRef;
  }

  /**
   * 判斷是否應該顯示退出團隊按鈕
   * 當前登入是個人租戶且登入的人和列表的數據一致時不顯示退出團隊按鈕
   */
  shouldShowExitButton(employee: any): boolean {
    return !!employee?.canExitTeam;
  }

  /**
   * 獲取員工角色
   * 根據員工信息判斷是拥有者還是秘書
   */
  getEmployeeRole(employee: any): string {
    return employee?.roleKey || 'tenant.employee.role-secretary';
  }

  get tableScrollConfig(): { x?: string | null; y?: string | null } {
    return this.embeddedMode ? {} : { y: '100%' };
  }

  trackByEmployee(index: number, employee: any): string {
    return employee?.id || employee?.accountId || employee?.name || `${index}`;
  }
}
